import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  TextField,
  Button,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  OpenInNew as OpenInNewIcon,
  Forward as ForwardIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import NavBar from "../../components/navBar/navBar";
import Loader from "../../components/Loader";
import useGetSupportConversationDetail from "../../hooks/useGetSupportConversationDetail";
import useSendSupportReplyMutation from "../../hooks/useSendSupportReplyMutation";
import useMarkSupportReadMutation from "../../hooks/useMarkSupportReadMutation";
import useGetSupportForwardCandidates from "../../hooks/useGetSupportForwardCandidates";
import useForwardSupportConversationMutation from "../../hooks/useForwardSupportConversationMutation";
import useCloseSupportConversationMutation from "../../hooks/useCloseSupportConversationMutation";
import { USER_ROLES } from "../../utils/constants";

dayjs.extend(utc);

const SupportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reply, setReply] = useState("");
  const [forwardOpen, setForwardOpen] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const isSuperAdmin = user_data?.role === USER_ROLES.SUPER_ADMIN;

  const { data: conversation, isLoading } = useGetSupportConversationDetail({ conversationId: id });
  const { mutate: sendReply, isPending: sending } = useSendSupportReplyMutation();
  const { mutate: markRead } = useMarkSupportReadMutation();
  const { data: forwardCandidates, isLoading: loadingCandidates } = useGetSupportForwardCandidates({
    conversationId: id,
    enabled: forwardOpen,
  });
  const { mutate: forwardConversation, isPending: forwarding } =
    useForwardSupportConversationMutation();
  const { mutate: closeConversation, isPending: closing } = useCloseSupportConversationMutation();

  useEffect(() => {
    if (id) markRead(id);
  }, [id]);

  const handleForward = () => {
    if (selectedCandidates.length === 0) return;
    forwardConversation(
      { conversationId: id, userIds: selectedCandidates.map((c) => c.id) },
      {
        onSuccess: () => {
          setForwardOpen(false);
          setSelectedCandidates([]);
        },
      }
    );
  };

  const handleClose = () => {
    closeConversation(id, { onSuccess: () => setCloseConfirmOpen(false) });
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation?.messages]);

  const handleSend = () => {
    const trimmed = reply.trim();
    if (!trimmed) return;
    sendReply({ conversationId: id, body: trimmed }, { onSuccess: () => setReply("") });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isLoading)
    return (
      <Box>
        <NavBar active="support" />
        <Loader />
      </Box>
    );

  if (!conversation) {
    return (
      <Box sx={{ maxWidth: "95%", mx: "auto" }}>
        <NavBar active="support" />
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary">Conversation not found.</Typography>
        </Box>
      </Box>
    );
  }

  const messages = conversation.messages ?? [];
  const isResolved = conversation.is_resolved;

  return (
    <Box sx={{ maxWidth: "95%", mx: "auto" }}>
      <NavBar active="support" />
      <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <IconButton onClick={() => navigate("/support")} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box flex={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" fontWeight="bold">
                {conversation.site_participant_id || conversation.patient_name}
              </Typography>
              <Chip
                label={isResolved ? "Resolved" : "Open"}
                color={isResolved ? "default" : "error"}
                size="small"
              />
              {conversation.patient_id && (
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={() =>
                    navigate(`/patient/${conversation.patient_id}/?trial=${conversation.trial_id}`)
                  }
                >
                  View Subject
                </Button>
              )}
              {isSuperAdmin && !isResolved && (
                <Button
                  size="small"
                  startIcon={<ForwardIcon fontSize="small" />}
                  onClick={() => setForwardOpen(true)}
                >
                  Forward
                </Button>
              )}
              {isSuperAdmin && !isResolved && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<CheckCircleIcon fontSize="small" />}
                  onClick={() => setCloseConfirmOpen(true)}
                >
                  Close Conversation
                </Button>
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {conversation.trial_name || "—"}
            </Typography>
            {conversation.participants?.length > 0 && (
              <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                {conversation.participants.map((p) => (
                  <Chip
                    key={p.id}
                    label={`${p.name} · ${p.role}`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Messages */}
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2, height: 460, overflowY: "auto", backgroundColor: "#fafafa" }}
        >
          {messages.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ mt: 10 }}>
              No messages yet.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {messages.map((msg) => {
                const isPatient = msg.sender_type === "patient";
                return (
                  <Box
                    key={msg.id}
                    sx={{ display: "flex", justifyContent: isPatient ? "flex-start" : "flex-end" }}
                  >
                    <Box
                      sx={{
                        maxWidth: "70%",
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        backgroundColor: isPatient ? "#f0f0f0" : "#fce4ec",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        {msg.sender_name} ·{" "}
                        {dayjs.utc(msg.created_at).local().format("MMM D, h:mm A")}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {msg.body}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Stack>
          )}
        </Paper>

        {/* Reply input */}
        {isResolved ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {conversation.resolved_by_name
                ? `Closed by ${conversation.resolved_by_name}.`
                : "This conversation has been resolved by the patient."}
            </Typography>
          </Paper>
        ) : (
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type your reply… (Enter to send)"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
            />
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={handleSend}
              disabled={!reply.trim() || sending}
              sx={{ flexShrink: 0 }}
            >
              Send
            </Button>
          </Stack>
        )}
      </Box>

      <Dialog open={forwardOpen} onClose={() => setForwardOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Forward Conversation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add site staff (Investigator, CRC, or Sub-Investigator) to this conversation. They will
            be able to view and reply, and will be notified by email and in-app notification.
          </Typography>
          <Autocomplete
            multiple
            id="forward-candidates-select"
            loading={loadingCandidates}
            value={selectedCandidates}
            options={forwardCandidates ?? []}
            getOptionLabel={(option) => `${option.name} (${option.role})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setSelectedCandidates(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} label={option.name} size="small" {...tagProps} />;
              })
            }
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder="Select staff to forward to" />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForwardOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleForward}
            disabled={selectedCandidates.length === 0 || forwarding}
          >
            Forward
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeConfirmOpen} onClose={() => setCloseConfirmOpen(false)}>
        <DialogTitle>Close Conversation</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will mark the conversation as resolved. The patient and any forwarded staff will no
            longer be able to send new messages. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleClose} disabled={closing}>
            Close Conversation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportDetail;
